import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Database, FolderTree, Users, MapPin, RefreshCw, AlertTriangle,
    Search, PlayCircle, ChevronDown, ChevronRight, Zap,
} from 'lucide-react';
import {
    getMigrationStatus,
    getMigrationSummary,
    migrateAll,
    migrateAllCategories,
    checkCategoriesStatus,
    migrateCategoryByNameAndType,
    checkCategoryByNameAndType,
    migrateAllProviders,
    getProvidersStats,
    migrateProviderByUsername,
    checkProviderByUsername,
    migrateAllLocations,
    checkLocationsStatus,
    migrateLocationByName,
} from '../../services/migrationApi';
import './MigrationPanel.css';

const TABS = [
    { key: 'general', label: 'General', icon: Database },
    { key: 'categories', label: 'Categorías', icon: FolderTree },
    { key: 'providers', label: 'Proveedores', icon: Users },
    { key: 'locations', label: 'Locations', icon: MapPin },
];

const isErrorPayload = (payload) =>
    payload && typeof payload === 'object' && typeof payload.error === 'string';

const MigrationPanel = () => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    const [systemStatus, setSystemStatus] = useState(null);
    const [systemSummary, setSystemSummary] = useState(null);
    const [categoriesStatus, setCategoriesStatus] = useState(null);
    const [providersStats, setProvidersStats] = useState(null);
    const [locationsStatus, setLocationsStatus] = useState(null);

    const [specificCategory, setSpecificCategory] = useState({ name: '', type: 'service' });
    const [specificProvider, setSpecificProvider] = useState({ username: '', type: 'service' });
    const [specificLocation, setSpecificLocation] = useState({ name: '' });

    useEffect(() => {
        loadInitialData();
    }, []);

    const safeRun = async (fn, setter) => {
        try {
            const data = await fn();
            setter(data);
        } catch (error) {
            console.error('Migration call failed:', error);
            const message = error?.response?.data?.message
                || error?.message
                || 'Backend no disponible';
            setter({ error: message });
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        await Promise.all([
            safeRun(getMigrationStatus, setSystemStatus),
            safeRun(getMigrationSummary, setSystemSummary),
            safeRun(checkCategoriesStatus, setCategoriesStatus),
            safeRun(getProvidersStats, setProvidersStats),
            safeRun(checkLocationsStatus, setLocationsStatus),
        ]);
        setLoading(false);
    };

    const handleMigrateAll = async () => {
        if (!window.confirm('¿Estás seguro de que querés migrar TODO el sistema? Esta operación puede tardar varios minutos.')) return;
        setLoading(true);
        try {
            await migrateAll();
            toast.success('Migración completa iniciada exitosamente');
            await loadInitialData();
        } catch (error) {
            console.error('Error migrating all:', error);
            toast.error('Error al iniciar la migración completa');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateAllCategories = async () => {
        if (!window.confirm('¿Migrar todas las categorías?')) return;
        setLoading(true);
        try {
            await migrateAllCategories();
            toast.success('Migración de categorías completada');
            await safeRun(checkCategoriesStatus, setCategoriesStatus);
        } catch (error) {
            console.error('Error migrating categories:', error);
            toast.error('Error al migrar categorías');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateSpecificCategory = async () => {
        if (!specificCategory.name.trim()) return toast.error('Ingresá el nombre de la categoría');
        setLoading(true);
        try {
            await migrateCategoryByNameAndType(specificCategory.name, specificCategory.type);
            toast.success(`Categoría "${specificCategory.name}" migrada`);
            await safeRun(checkCategoriesStatus, setCategoriesStatus);
        } catch (error) {
            toast.error('Error al migrar la categoría');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckSpecificCategory = async () => {
        if (!specificCategory.name.trim()) return toast.error('Ingresá el nombre de la categoría');
        setLoading(true);
        try {
            const result = await checkCategoryByNameAndType(specificCategory.name, specificCategory.type);
            toast.success('Verificación completada');
            console.log('Category check:', result);
        } catch (error) {
            toast.error('Error al verificar la categoría');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateAllProviders = async () => {
        if (!window.confirm('¿Migrar todos los proveedores?')) return;
        setLoading(true);
        try {
            await migrateAllProviders();
            toast.success('Migración de proveedores completada');
            await safeRun(getProvidersStats, setProvidersStats);
        } catch (error) {
            toast.error('Error al migrar proveedores');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateSpecificProvider = async () => {
        if (!specificProvider.username.trim()) return toast.error('Ingresá el username');
        setLoading(true);
        try {
            await migrateProviderByUsername(specificProvider.username, specificProvider.type);
            toast.success(`Proveedor ${specificProvider.username} migrado`);
        } catch (error) {
            toast.error('Error al migrar el proveedor');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckSpecificProvider = async () => {
        if (!specificProvider.username.trim()) return toast.error('Ingresá el username');
        setLoading(true);
        try {
            const result = await checkProviderByUsername(specificProvider.username, specificProvider.type);
            toast.success('Verificación completada');
            console.log('Provider check:', result);
        } catch (error) {
            toast.error('Error al verificar el proveedor');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateAllLocations = async () => {
        if (!window.confirm('¿Migrar todas las locations?')) return;
        setLoading(true);
        try {
            await migrateAllLocations();
            toast.success('Migración de locations completada');
            await safeRun(checkLocationsStatus, setLocationsStatus);
        } catch (error) {
            toast.error('Error al migrar locations');
        } finally {
            setLoading(false);
        }
    };

    const handleMigrateSpecificLocation = async () => {
        if (!specificLocation.name.trim()) return toast.error('Ingresá el nombre de la location');
        setLoading(true);
        try {
            await migrateLocationByName(specificLocation.name);
            toast.success(`Location ${specificLocation.name} migrada`);
        } catch (error) {
            toast.error('Error al migrar la location');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="migration-panel">
            {loading && (
                <div className="migration-panel__overlay" role="status" aria-live="polite">
                    <RefreshCw size={28} className="admin-spin" />
                    <p>Procesando…</p>
                </div>
            )}

            <header className="migration-panel__header">
                <div>
                    <h1 className="migration-panel__title">Migración del Sistema</h1>
                    <p className="migration-panel__subtitle">
                        Migra categorías, proveedores y locations al nuevo esquema de IDs.
                        Operaciones potencialmente costosas — usar con cuidado.
                    </p>
                </div>
                <button
                    type="button"
                    className="migration-panel__refresh-btn"
                    onClick={loadInitialData}
                    disabled={loading}
                >
                    <RefreshCw size={16} className={loading ? 'admin-spin' : ''} />
                    <span>Actualizar datos</span>
                </button>
            </header>

            <nav className="migration-panel__tabs" role="tablist" aria-label="Secciones de migración">
                {TABS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === key}
                        className={`migration-panel__tab ${activeTab === key ? 'is-active' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        <Icon size={16} />
                        <span>{label}</span>
                    </button>
                ))}
            </nav>

            <div className="migration-panel__body">
                {activeTab === 'general' && (
                    <div className="migration-panel__grid">
                        <StatusCard title="Estado del sistema" payload={systemStatus} loading={loading} />
                        <StatusCard title="Resumen ejecutivo" payload={systemSummary} loading={loading} />

                        <ActionCard
                            tone="danger"
                            title="Migración completa"
                            description="Migra TODOS los componentes (categorías, proveedores y locations). Esta operación puede tardar varios minutos."
                            actionLabel="Migrar todo el sistema"
                            actionIcon={Zap}
                            onAction={handleMigrateAll}
                            disabled={loading}
                        />
                    </div>
                )}

                {activeTab === 'categories' && (
                    <div className="migration-panel__grid">
                        <StatusCard title="Estado de categorías" payload={categoriesStatus} loading={loading} />

                        <ActionCard
                            tone="primary"
                            title="Migrar todas las categorías"
                            description="Convierte todas las categorías al nuevo esquema basado en UUID."
                            actionLabel="Migrar todas"
                            actionIcon={PlayCircle}
                            onAction={handleMigrateAllCategories}
                            disabled={loading}
                        />

                        <SpecificForm
                            title="Categoría específica"
                            description="Migrar o verificar una categoría individual."
                            inputs={[
                                {
                                    type: 'text',
                                    placeholder: 'Nombre de la categoría',
                                    value: specificCategory.name,
                                    onChange: (v) => setSpecificCategory({ ...specificCategory, name: v }),
                                },
                                {
                                    type: 'select',
                                    value: specificCategory.type,
                                    onChange: (v) => setSpecificCategory({ ...specificCategory, type: v }),
                                    options: [
                                        { value: 'service', label: 'Servicios' },
                                        { value: 'products', label: 'Productos' },
                                    ],
                                },
                            ]}
                            actions={[
                                { label: 'Verificar', icon: Search, onClick: handleCheckSpecificCategory, variant: 'secondary' },
                                { label: 'Migrar', icon: PlayCircle, onClick: handleMigrateSpecificCategory, variant: 'primary' },
                            ]}
                            disabled={loading}
                        />
                    </div>
                )}

                {activeTab === 'providers' && (
                    <div className="migration-panel__grid">
                        <StatusCard title="Estadísticas de proveedores" payload={providersStats} loading={loading} />

                        <ActionCard
                            tone="primary"
                            title="Migrar todos los proveedores"
                            description="Convierte todos los proveedores al nuevo esquema."
                            actionLabel="Migrar todos"
                            actionIcon={PlayCircle}
                            onAction={handleMigrateAllProviders}
                            disabled={loading}
                        />

                        <SpecificForm
                            title="Proveedor específico"
                            description="Migrar o verificar un proveedor individual por username."
                            inputs={[
                                {
                                    type: 'text',
                                    placeholder: 'Username del proveedor',
                                    value: specificProvider.username,
                                    onChange: (v) => setSpecificProvider({ ...specificProvider, username: v }),
                                },
                                {
                                    type: 'select',
                                    value: specificProvider.type,
                                    onChange: (v) => setSpecificProvider({ ...specificProvider, type: v }),
                                    options: [
                                        { value: 'service', label: 'Servicios' },
                                        { value: 'products', label: 'Productos' },
                                    ],
                                },
                            ]}
                            actions={[
                                { label: 'Verificar', icon: Search, onClick: handleCheckSpecificProvider, variant: 'secondary' },
                                { label: 'Migrar', icon: PlayCircle, onClick: handleMigrateSpecificProvider, variant: 'primary' },
                            ]}
                            disabled={loading}
                        />
                    </div>
                )}

                {activeTab === 'locations' && (
                    <div className="migration-panel__grid">
                        <StatusCard title="Estado de locations" payload={locationsStatus} loading={loading} />

                        <ActionCard
                            tone="primary"
                            title="Migrar todas las locations"
                            description="Convierte todas las locations al nuevo esquema."
                            actionLabel="Migrar todas"
                            actionIcon={PlayCircle}
                            onAction={handleMigrateAllLocations}
                            disabled={loading}
                        />

                        <SpecificForm
                            title="Location específica"
                            description="Migrar una location individual por nombre."
                            inputs={[
                                {
                                    type: 'text',
                                    placeholder: 'Nombre de la location',
                                    value: specificLocation.name,
                                    onChange: (v) => setSpecificLocation({ name: v }),
                                },
                            ]}
                            actions={[
                                { label: 'Migrar location', icon: PlayCircle, onClick: handleMigrateSpecificLocation, variant: 'primary' },
                            ]}
                            disabled={loading}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- Sub-componentes ----------

const StatusCard = ({ title, payload, loading }) => {
    const [showRaw, setShowRaw] = useState(true);

    if (loading && !payload) {
        return (
            <section className="mig-card mig-card--span-2">
                <header className="mig-card__header">
                    <h3 className="mig-card__title">{title}</h3>
                </header>
                <div className="mig-card__body">
                    <div className="mig-empty">Cargando…</div>
                </div>
            </section>
        );
    }

    if (!payload) {
        return (
            <section className="mig-card mig-card--span-2">
                <header className="mig-card__header">
                    <h3 className="mig-card__title">{title}</h3>
                </header>
                <div className="mig-card__body">
                    <div className="mig-empty">Sin datos.</div>
                </div>
            </section>
        );
    }

    if (isErrorPayload(payload)) {
        return (
            <section className="mig-card mig-card--span-2">
                <header className="mig-card__header">
                    <h3 className="mig-card__title">{title}</h3>
                </header>
                <div className="mig-card__body">
                    <div className="mig-alert mig-alert--danger">
                        <AlertTriangle size={16} />
                        <span>{payload.error}</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="mig-card mig-card--span-2">
            <header className="mig-card__header">
                <h3 className="mig-card__title">{title}</h3>
                <button
                    type="button"
                    className="mig-card__toggle"
                    onClick={() => setShowRaw((s) => !s)}
                    aria-expanded={showRaw}
                >
                    {showRaw ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span>{showRaw ? 'Ocultar JSON' : 'Mostrar JSON'}</span>
                </button>
            </header>
            <div className="mig-card__body">
                <KeyValueGrid payload={payload} />
                {showRaw && (
                    <pre className="mig-json">{JSON.stringify(payload, null, 2)}</pre>
                )}
            </div>
        </section>
    );
};

const KeyValueGrid = ({ payload }) => {
    if (!payload || typeof payload !== 'object') return null;
    const entries = Object.entries(payload).filter(([_, v]) => typeof v !== 'object' || v === null);
    if (entries.length === 0) return null;
    return (
        <div className="mig-kv-grid">
            {entries.map(([k, v]) => (
                <div key={k} className="mig-kv">
                    <div className="mig-kv__key">{k}</div>
                    <div className="mig-kv__value">{String(v ?? '—')}</div>
                </div>
            ))}
        </div>
    );
};

const ActionCard = ({ tone = 'primary', title, description, actionLabel, actionIcon: Icon, onAction, disabled }) => (
    <section className={`mig-card mig-card--${tone}`}>
        <header className="mig-card__header">
            <h3 className="mig-card__title">{title}</h3>
        </header>
        <div className="mig-card__body">
            <p className="mig-card__desc">{description}</p>
            <button
                type="button"
                className={`mig-btn mig-btn--${tone}`}
                onClick={onAction}
                disabled={disabled}
            >
                {Icon && <Icon size={16} />}
                <span>{actionLabel}</span>
            </button>
        </div>
    </section>
);

const SpecificForm = ({ title, description, inputs = [], actions = [], disabled }) => (
    <section className="mig-card mig-card--span-2">
        <header className="mig-card__header">
            <h3 className="mig-card__title">{title}</h3>
        </header>
        <div className="mig-card__body">
            <p className="mig-card__desc">{description}</p>
            <div className="mig-form-row">
                {inputs.map((input, idx) => {
                    if (input.type === 'select') {
                        return (
                            <select
                                key={idx}
                                className="mig-input"
                                value={input.value}
                                onChange={(e) => input.onChange(e.target.value)}
                                disabled={disabled}
                            >
                                {input.options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        );
                    }
                    return (
                        <input
                            key={idx}
                            type={input.type || 'text'}
                            className="mig-input"
                            placeholder={input.placeholder}
                            value={input.value}
                            onChange={(e) => input.onChange(e.target.value)}
                            disabled={disabled}
                        />
                    );
                })}
            </div>
            <div className="mig-form-actions">
                {actions.map((a, idx) => (
                    <button
                        key={idx}
                        type="button"
                        className={`mig-btn mig-btn--${a.variant || 'primary'}`}
                        onClick={a.onClick}
                        disabled={disabled}
                    >
                        {a.icon && <a.icon size={14} />}
                        <span>{a.label}</span>
                    </button>
                ))}
            </div>
        </div>
    </section>
);

export default MigrationPanel;
